import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import { DisplayLayout } from 'components/Collection/Collection.const';
import { isTouch } from 'util/device';

import NoResults from 'components/Search/NoResults';
import Collection from 'components/Collection';
import Loader from 'components/Loader';
import RecentSearchCollection from '../RecentSearchCollection';
import PopularSearch from './PopularSearch/index';
import TEST_ID from './Results.const';
import { TILE_COMPONENT_TYPES } from 'components/TileComponent/TileComponent.const';
import { ALL_CLIP_COLLECTION_SLUG } from 'globalConst/const';

import styles from './Results.styles';
import { createUseStyles } from 'react-jss';
import { useFetchData } from '../../hooks';

const useStyles = createUseStyles(styles, { name: 'Results' });
const Results = ({
  isActive,
  hasRecentSearchItems,
  stationResults,
  genreResults,
  moodResults,
  podcastResults,
  liveShowResults,
  onDemandResults,
  setActive,
  isLoading,
  query,
}) => {
  const classes = useStyles();

  const {
    data: clipCollection,
    loadMore: loadMoreClips,
    hasMoreItems: hasMoreClips,
    nextLoadCount: nextLoadClipsCount,
    isLoading: isClipsLoading,
  } = useFetchData({
    contentTypeId: 'searchClipCollection',
    slug: ALL_CLIP_COLLECTION_SLUG,
    query: query && query.length > 2 ? query : null,
  });

  const clips = clipCollection && clipCollection.items;

  const renderResults = () => {
    return (
      <Fragment>
        {stationResults && stationResults.length > 0 && (
          <Collection
            uuid="results-stations"
            title="Stations"
            items={stationResults}
            tileComponent={TILE_COMPONENT_TYPES.STATION}
            rows={1}
            display={DisplayLayout.CAROUSEL}
            showNowPlayingInfo
            swipe
            isInSearch
            hasMoreItems={hasMoreClips}
            nextLoadCount={nextLoadClipsCount}
          />
        )}
        {podcastResults && podcastResults.length > 0 && (
          <div>
            <Collection
              uuid="results-podcasts"
              title="Podcasts"
              items={podcastResults}
              tileComponent={TILE_COMPONENT_TYPES.PODCAST}
              rows={1}
              display={DisplayLayout.CAROUSEL}
              swipe
              isInSearch
            />
          </div>
        )}
        {clips && clips.length > 0 && (
          <Collection
            uuid="results-clips"
            title="Podcast afleveringen"
            tileComponent={TILE_COMPONENT_TYPES.ON_DEMAND}
            isInSearch
            {...clipCollection}
            loadMore={loadMoreClips}
            isComponentLoading={isClipsLoading}
          />
        )}

        {genreResults && genreResults.length > 0 && (
          <Collection
            uuid="results-genres"
            title="Genres"
            items={genreResults}
            tileComponent={TILE_COMPONENT_TYPES.TAG}
            rows={1}
            display={DisplayLayout.CAROUSEL}
            swipe
            isInSearch
          />
        )}
        {moodResults && moodResults.length > 0 && (
          <Collection
            uuid="results-moods"
            title="Moods & momenten"
            items={moodResults}
            tileComponent={TILE_COMPONENT_TYPES.TAG}
            rows={1}
            display={DisplayLayout.CAROUSEL}
            swipe
            isInSearch
          />
        )}
        {liveShowResults && liveShowResults.length > 0 && (
          <Collection
            uuid="results-live-shows"
            title="Live Shows"
            items={liveShowResults}
            tileComponent={TILE_COMPONENT_TYPES.SHOW}
            rows={1}
            display={DisplayLayout.CAROUSEL}
            swipe
            isInSearch
          />
        )}
        {onDemandResults && onDemandResults.length > 0 && (
          <Collection
            uuid="results-ondemand-shows"
            title="On Demand Shows"
            items={onDemandResults}
            tileComponent={TILE_COMPONENT_TYPES.ON_DEMAND}
            rows={1}
            display={DisplayLayout.CAROUSEL}
            swipe
            showCollectionLink={false}
            isInSearch
          />
        )}
      </Fragment>
    );
  };

  if (!isActive) {
    return null;
  }

  if (isLoading) {
    return (
      <div data-testid={TEST_ID.SEARCH_LOADER} className={classes.loaderScreen}>
        <Loader />
      </div>
    );
  }

  const loadIntroSearch = isActive && (isLoading || query.length < 1);

  const noResultsFromSearch =
    stationResults &&
    stationResults.length === 0 &&
    genreResults &&
    genreResults.length === 0 &&
    moodResults &&
    moodResults.length === 0 &&
    podcastResults &&
    podcastResults.length === 0 &&
    liveShowResults &&
    liveShowResults.length === 0 &&
    onDemandResults &&
    onDemandResults.length === 0 &&
    !loadIntroSearch &&
    (!clips || clips.length === 0);

  return (
    <Fragment>
      {noResultsFromSearch && <NoResults />}
      {loadIntroSearch && (
        <div className={classes.containerRecentPopular} data-testid={TEST_ID.INTRO_SEARCH}>
          {hasRecentSearchItems && <RecentSearchCollection isInSearch />}
          <PopularSearch data-testid={TEST_ID.POPULAIR_SEARCH} />
        </div>
      )}
      <div
        data-testid={TEST_ID.RESULT_CONTAINER}
        className={classNames(classes.container, {
          [classes.containerMobile]: isTouch && !loadIntroSearch,
        })}
      >
        {!loadIntroSearch && renderResults()}
      </div>
      <button
        data-testid={TEST_ID.SCREEN_READER_BUTTON}
        className={classes.screenReadersOnly}
        onBlur={() => setActive(false)}
        type="button"
      />
    </Fragment>
  );
};

Results.propTypes = {
  isActive: PropTypes.bool.isRequired,
  hasRecentSearchItems: PropTypes.bool,
  stationResults: PropTypes.array,
  genreResults: PropTypes.array,
  moodResults: PropTypes.array,
  podcastResults: PropTypes.array,
  liveShowResults: PropTypes.array,
  onDemandResults: PropTypes.array,
  setActive: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  query: PropTypes.string,
};

Results.defaultProps = {
  stationResults: null,
  genreResults: null,
  moodResults: null,
  podcastResults: null,
  liveShowResults: null,
  onDemandResults: null,
  isLoading: false,
  query: null,
  hasRecentSearchItems: false,
};

export default Results;
